import { useEffect, useRef, useState } from "react";
import Seo from "@/components/Seo";
import Header from "@/components/landing/Header";
import IndustryStrip from "@/components/landing/IndustryStrip";
import Footer from "@/components/landing/Footer";
import { trades } from "@/data/trades";
import { PhoneMissed, Play, RotateCcw, CheckCircle2, Loader2 } from "lucide-react";

const SCRIPTS = {
  hvac: {
    name: "HVAC",
    qualifiedLabel: "Qualified: emergency AC repair · booked 2–4 PM",
    chat: [
      { from: "backfill", text: "Hi, this is Apex Heating & Air — sorry we missed your call. How can we help?" },
      { from: "customer", text: "AC stopped cooling and it's 95° inside!" },
      { from: "backfill", text: "Sorry about that — sounds like an emergency repair. Is anyone home right now?" },
      { from: "customer", text: "Yes, we're home all day." },
      { from: "backfill", text: "A dispatcher has been looped in. Earliest tech window: today, 2–4 PM. Confirm?" },
    ],
  },
  plumbers: { name: "Plumbers", qualifiedLabel: trades.plumbers.hero.qualifiedLabel, chat: trades.plumbers.chat },
  electricians: { name: "Electricians", qualifiedLabel: trades.electricians.hero.qualifiedLabel, chat: trades.electricians.chat },
  roofers: { name: "Roofers", qualifiedLabel: trades.roofers.hero.qualifiedLabel, chat: trades.roofers.chat },
  contractors: { name: "General Contractors", qualifiedLabel: trades.contractors.hero.qualifiedLabel, chat: trades.contractors.chat },
};

const CONFIRM_MSG = { from: "customer", text: "Confirmed — see you then!" };
const inputCls =
  "w-full rounded-md border border-white/[0.1] bg-[#0B0D10] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors focus:border-[#F25F22]/60 focus:ring-2 focus:ring-[#F25F22]/20";

export default function Demo() {
  const [company, setCompany] = useState("");
  const [trade, setTrade] = useState("hvac");
  const [step, setStep] = useState(-1); // -1 idle, 0 missed-call banner, 1..N messages, N+1 booked
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);

  const script = SCRIPTS[trade].chat.map((m, i) =>
    i === 0 && company.trim()
      ? { ...m, text: m.text.replace(/this is .+? — sorry/, `this is ${company.trim()} — sorry`) }
      : m
  );
  const allMessages = [...script, CONFIRM_MSG];
  const bookedStep = allMessages.length + 1;
  const running = step >= 0 && step < bookedStep;
  const done = step >= bookedStep;

  useEffect(() => {
    if (step < 0 || step > bookedStep) return;
    const isBackfill = step >= 1 && step <= allMessages.length && allMessages[step - 1]?.from === "backfill";
    const delay = step === 0 ? 1800 : step === bookedStep ? 1200 : isBackfill ? 1400 : 1500;
    if (isBackfill) setTyping(true);
    const t = setTimeout(() => {
      setTyping(false);
      setStep((s) => s + 1);
    }, delay);
    return () => clearTimeout(t);
  }, [step, trade, company]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [step, typing]);

  const run = () => setStep(0);
  const reset = () => { setStep(-1); setTyping(false); };
  const displayCompany = company.trim() || "Your Company";

  return (
    <>
      <Seo
        title="Automatic Demo — Watch Backfill Book a Job by Itself"
        description="Enter your company name and watch Backfill answer a missed call, qualify the customer, and book the job — automatically, with your branding. No sales call required."
      />
      <Header />
      <IndustryStrip />
      <main>
        <section data-testid="demo-page" className="noise relative overflow-hidden py-16 sm:py-20">
          <div className="bg-grid absolute inset-0 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,black,transparent)]" />
          <div className="relative mx-auto grid max-w-7xl items-start gap-12 px-5 sm:px-8 lg:grid-cols-2">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#F25F22]">
                The automatic demo
              </p>
              <h1 className="mt-4 font-display text-4xl font-bold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl">
                Watch Backfill book a job.{" "}
                <span className="text-[#F25F22]">You do nothing.</span>
              </h1>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-400">
                Put your company name in, pick your trade, and watch a missed call become
                a booked job — exactly how it runs for real customers. No sales call,
                no scheduling, no waiting on us.
              </p>

              <div className="mt-8 space-y-4">
                <div>
                  <label htmlFor="demo-company" className="text-sm font-semibold text-slate-200">
                    Your company name
                  </label>
                  <input
                    id="demo-company"
                    value={company}
                    onChange={(e) => { setCompany(e.target.value); reset(); }}
                    data-testid="demo-company-input"
                    placeholder="e.g. Blackburn Heating & Air"
                    maxLength={60}
                    className={`${inputCls} mt-2`}
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-200">Your trade</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {Object.entries(SCRIPTS).map(([slug, t]) => (
                      <button
                        key={slug}
                        onClick={() => { setTrade(slug); reset(); }}
                        data-testid={`demo-trade-${slug}`}
                        className={`rounded-full border px-3.5 py-2 font-mono text-[11px] uppercase tracking-wider transition-colors ${
                          trade === slug
                            ? "border-[#F25F22] bg-[#F25F22]/10 text-[#F25F22]"
                            : "border-white/[0.1] text-slate-400 hover:text-slate-100"
                        }`}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-1">
                  <button
                    onClick={run}
                    disabled={running}
                    data-testid="demo-run-button"
                    className="flex items-center gap-2 rounded-md bg-[#F25F22] px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-[#FF7539] hover:shadow-[0_0_24px_rgba(242,95,34,0.45)] disabled:opacity-50"
                  >
                    {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                    {running ? "Running…" : step >= 0 ? "Run it again" : "Run my demo"}
                  </button>
                  {step >= 0 && !running && (
                    <button
                      onClick={run}
                      data-testid="demo-replay-button"
                      className="flex items-center gap-2 rounded-md border border-white/[0.12] px-4 py-3 text-sm font-medium text-slate-300 hover:text-slate-50"
                    >
                      <RotateCcw className="h-4 w-4" /> Replay
                    </button>
                  )}
                </div>
                <p className="font-mono text-[11px] uppercase tracking-wider text-slate-600">
                  Simulated demo — real texts once your phone line is connected
                </p>
                <p className="text-sm text-slate-500">
                  Want to drive instead of watch?{" "}
                  <a href="/playground" data-testid="demo-playground-link" className="font-semibold text-[#F25F22] underline-offset-4 hover:text-[#FF7539] hover:underline">
                    Open the interactive playground
                  </a>
                </p>
              </div>

              {done && (
                <div data-testid="demo-results" className="mt-8 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-6">
                  <p className="font-display text-lg font-bold text-emerald-300">
                    Missed call → booked job. Zero minutes of your time.
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">
                    That ran start to finish without a dispatcher. This is what happens at 2 PM
                    when your whole crew is on roofs — and at 2 AM when everyone&apos;s asleep.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <a
                      href="/signup?plan=growth&billing=monthly"
                      data-testid="demo-signup-cta"
                      className="rounded-md bg-[#F25F22] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#FF7539]"
                    >
                      Put this on my phones
                    </a>
                    <a
                      href="/#demo"
                      data-testid="demo-human-cta"
                      className="rounded-md border border-white/[0.12] px-5 py-3 text-sm font-medium text-slate-200 transition-colors hover:border-[#F25F22]/50"
                    >
                      Talk to a human first
                    </a>
                  </div>
                </div>
              )}
            </div>

            <div className="mx-auto w-full max-w-sm">
              <div data-testid="demo-phone" className="rounded-[2rem] border border-white/[0.1] bg-[#0B0D10] p-3 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7)]">
                <div className="overflow-hidden rounded-[1.5rem] bg-[#12161F]">
                  <div className="border-b border-white/[0.06] px-5 py-3.5 text-center">
                    <p className="font-display text-sm font-bold text-slate-100">{displayCompany}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      Backfill line · always on
                    </p>
                  </div>
                  <div ref={scrollRef} className="flex h-[420px] flex-col justify-end space-y-2.5 overflow-y-auto px-4 py-4">
                    {step === -1 && (
                      <p className="py-16 text-center text-sm text-slate-600">
                        Your demo plays here — hit{" "}
                        <span className="text-[#F25F22]">Run my demo</span>
                      </p>
                    )}
                    {step >= 0 && (
                      <div className="flex items-center gap-2.5 rounded-lg border border-red-500/30 bg-red-500/[0.08] px-3.5 py-2.5">
                        <PhoneMissed className="h-4 w-4 shrink-0 text-red-400" />
                        <div>
                          <p className="text-xs font-semibold text-red-300">Missed call · (555) 014-2231</p>
                          <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                            Auto text-back in 8 seconds
                          </p>
                        </div>
                      </div>
                    )}
                    {allMessages.slice(0, Math.max(0, step)).map((m, i) => (
                      <div key={i} className={`flex ${m.from === "customer" ? "justify-end" : "justify-start"}`}>
                        <div
                          data-testid={`demo-message-${i}`}
                          className={`max-w-[85%] rounded-xl px-3.5 py-2 text-[13px] leading-relaxed ${
                            m.from === "customer"
                              ? "bg-[#F25F22] text-white"
                              : "border border-white/[0.08] bg-[#1A212E] text-slate-200"
                          }`}
                        >
                          {m.text}
                        </div>
                      </div>
                    ))}
                    {typing && (
                      <div className="flex justify-start">
                        <div className="rounded-xl border border-white/[0.08] bg-[#1A212E] px-4 py-3 font-mono text-slate-500">
                          ···
                        </div>
                      </div>
                    )}
                    {done && (
                      <div
                        data-testid="demo-booked-card"
                        className="flex items-center gap-2.5 rounded-lg border border-emerald-500/40 bg-emerald-500/[0.1] px-3.5 py-2.5"
                      >
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
                        <p className="text-xs font-semibold text-emerald-300">
                          {SCRIPTS[trade].qualifiedLabel}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
